<template>
  <!-- this is a recursive component and will always be part of an unsorted list -->
  <div
    class="nav__item"
    :class="[`nav__item--depth-${depth}`, `nav__item--${item.accent}`]"
    :id="itemId"
  >
    <!-- the link to access the page -->
    <a
      class="nav__link"
      :class="{ 'nav__link--has-children': hasChildren, 'nav__link--open': isOpen }"
      role="menuitem"
      :href="item.path"
      :aria-haspopup="hasChildren ? 'menu' : undefined"
      ref="linkRef"
      @click="handleClick($event)"
      @focusin="handleFocusIn()"
    >
      <!-- label -->
      <span class="nav__link__label"
        >{{ sanitize(item.title) }}

        <!-- if the page has child pages we show a chevron -->
        <span
          class="nav__link__icon nav__link__icon--chevron"
          :class="{ 'nav__link__icon--rotate': isOpen }"
          v-if="hasChildren && depth !== 0"
        >
          <IconChevron /> </span
      ></span>
      <span class="nav__link__icon nav__link__icon--minus" v-if="isOpen && depth !== 0">
        <IconMinus />
      </span>
    </a>

    <!-- sub navigation which will only be visible if the item has child routes -->
    <ul
      v-if="hasChildren"
      role="menu"
      :aria-label="'Untermenü ' + item.title"
      class="nav__submenu"
      :class="{ 'nav__submenu--open': isOpen }"
      ref="submenuRef"
      :style="{ height: isOpen ? menuHeight : '0' }"
      @transitionend="setHeightToAuto()"
    >
      <!-- since every header is the collapse of sub menu we add another item to access the page -->
      <NavItemMobile
        :index="0"
        :depth="depth + 1"
        @open-parent-menu="openMenu()"
        :item="getOverviewItem"
      ></NavItemMobile>

      <!-- this component since it is recursive -->
      <NavItemMobile
        v-for="(child, i) in item.children"
        :key="child.path"
        :item="child"
        :index="i + 1"
        @scroll-to="bubbleUp($event)"
        :depth="depth + 1"
        @open-parent-menu="openMenu()"
      />
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { type ISubHeaderNavItem, sanitize } from '../helper';

import NavItemMobile from './NavItemMobile.ce.vue';

import IconChevron from '~icons/mmm/chevron-right.svg';
import IconMinus from '~icons/mmm/minus.svg';

const props = defineProps<{
  item: ISubHeaderNavItem;
  hasBorder?: boolean;
  depth: number;
  index: number;
}>();

const isOpen = ref<boolean>(false);
const submenuRef = ref<HTMLElement | null>(null);
const linkRef = ref<HTMLElement | null>(null);
const menuHeight = ref('0');

const emit = defineEmits(['scrollTo', 'openParentMenu']);

const getOverviewItem = computed(() => {
  return {
    ...props.item,
    children: [],
    title: `Übersicht ${props.item.title}`,
  };
});

function handleClick(e: Event | null = null) {
  if (!hasChildren.value) return;
  if (e) {
    e.preventDefault();
  }
  if (isOpen.value) {
    menuHeight.value = submenuRef.value?.clientHeight + 'px';
    setTimeout(() => {
      menuHeight.value = '0';
      isOpen.value = !isOpen.value;
    });
  } else {
    menuHeight.value = submenuRef.value?.scrollHeight + 'px' || '0';
    isOpen.value = !isOpen.value;
  }
}

function bubbleUp(target: string) {
  emit('scrollTo', target);
}

function handleFocusIn() {
  emit('openParentMenu');
}

function setHeightToAuto() {
  menuHeight.value = 'auto';
  if (isOpen.value) {
    emit('scrollTo', `#${itemId.value}`);
  }
}

function openMenu() {
  if (!isOpen.value) {
    handleClick();
  }
}

const hasChildren = computed(() => props.item.children && props.item.children?.length > 0);

const itemId = computed(() => {
  return `nav-item-${props.depth}-${props.index}`;
});

function focus() {
  linkRef.value?.focus();
}

defineExpose({
  focus,
});

export interface INavItemMobile {
  focus(): void;
}
</script>

<style lang="scss" scoped>
@use '../../../common/styles/utils/utils';

.nav__item {
  background: transparent;
  color: var(--mobile-menu-color);
  font-size: utils.px-to-rem(16);
  font-weight: 400;

  &--depth-0 {
    font-size: utils.px-to-rem(18);

    &.nav__item--first {
      --current-accent: var(--first-accent);
    }

    &.nav__item--second {
      --current-accent: var(--second-accent);
    }

    &.nav__item--third {
      --current-accent: var(--third-accent);
    }

    &.nav__item--forth {
      --current-accent: var(--forth-accent);
    }
    > .nav__link {
      border-left: 5px solid transparent;
      text-transform: uppercase;
      color: var(--mobile-menu-color);
    }
  }
}

.nav__link {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: utils.px-to-rem(18);
  border-bottom: 1px solid var(--mobile-border-color);
  text-decoration: none;
  font-weight: bold;
  color: var(--default-text-color);

  &__label {
    display: flex;
    gap: 8px;

    .nav__item--depth-2 & {
      padding-left: 32px;
    }

    .nav__item--depth-3 & {
      padding-left: 44px;
    }

    .nav__item--depth-4 & {
      padding-left: 56px;
    }

    .nav__item--depth-5 & {
      padding-left: 68px;
    }
  }

  &--open {
    background-color: var(--color-background-background);
    color: var(--current-accent) !important;

    .nav__item--depth-0 & {
      border-left-color: var(--current-accent);
    }
  }

  &__icon {
    transition: transform var(--transform-function) 0.2s;

    &--rotate {
      transform: rotate(90deg);
    }

    &--chevron {
      svg {
        height: utils.px-to-rem(15);
        width: utils.px-to-rem(15);
      }
    }

    &--minus {
      transform: translateY(2px);
      svg {
        height: utils.px-to-rem(18);
        width: utils.px-to-rem(18);
      }
    }
  }
}

.nav__submenu {
  background-color: var(--overlay-background);
  color: var(--default-text-color);
  padding: 0;
  margin: 0;
  list-style: none;
  height: 0;
  overflow: hidden;
  transition: all ease-in-out 0.2s;
  padding: 0 0;
}
</style>
